import {
  BottomSheetFlatList,
  type BottomSheetFlatListMethods,
  BottomSheetTextInput,
} from '@gorhom/bottom-sheet'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Keyboard,
  type LayoutChangeEvent,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  Pressable,
  Text as RNText,
  StyleSheet,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native'
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { ScrollFade, useScrollEdgeFades } from '../components/ScrollFade'
import { Drawer } from '../flow/Drawer'
import { useKeyboardHeight } from '../flow/useKeyboardHeight'
import type { NestedSheet } from '../hooks/useNestedSheet'
import { useNestedSheetHeight } from '../hooks/useNestedSheetHeight'
import { usePressScale } from '../hooks/usePressScale'
import {
  FigmaCheckIcon,
  FigmaCloseIcon,
  FigmaSearchIcon,
} from '../icons/FigmaSystemIcons'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { Box, Text, useTheme } from '../theme/restyle'
import { flatTextStyle } from '../theme/textStyle'
import type { FunkitTheme } from '../theme/toRestyleTheme'
import { CONTENT_FADE_DURATION } from '../utils/motion'
import { FiatCountryIndexRail } from './FiatCountryIndexRail'
import {
  type EntryHeights,
  type ListEntry,
  countryListEntries,
  measureEntries,
  sectionAt,
} from './countryListEntries'
import { countrySections } from './countrySections'
import {
  PHONE_COUNTRIES,
  type PhoneCountry,
  countryFlag,
} from './phoneCountries'

export interface FiatCountrySheetProps {
  sheet: NestedSheet<'countries'>
  /** The code the field carries; absent until a field has raised the list. */
  selected?: PhoneCountry
  onSelect: (country: PhoneCountry) => void
}

/**
 * The country list as a stacked modal layer over the step that raised it
 * (2531:8471).
 *
 * A drawer rather than a panel in the keyboard's slot: every country on earth
 * is further than a panel's worth of room can be read through, so the design
 * gives the list the whole screen and its own search.
 */
export function FiatCountrySheet({
  sheet,
  selected,
  onSelect,
}: FiatCountrySheetProps) {
  return (
    // The list is the screen; it scrolls through gorhom's own scrollable,
    // which cannot be measured (see sheetSizing).
    <Drawer sheet={sheet} fullHeight>
      {sheet.nestedScreen === 'countries' && selected !== undefined ? (
        <FiatCountryList
          selected={selected}
          onSelect={onSelect}
          onClose={sheet.closeNestedScreen}
        />
      ) : null}
    </Drawer>
  )
}

/**
 * What the sheet holds: the search the list is filtered by, and the countries
 * grouped under the initial they file under.
 */
export function FiatCountryList({
  selected,
  onSelect,
  heading,
  onClose,
}: {
  selected: PhoneCountry
  onSelect: (country: PhoneCountry) => void
  /** The question the list answers, for a field that asks one of its own. */
  heading?: string
  onClose: () => void
}) {
  const { t, locale } = useFunkitTranslation()
  const theme = useTheme()
  const insets = useSafeAreaInsets()
  const height = useNestedSheetHeight()
  const keyboardHeight = useKeyboardHeight(true)
  const [query, setQuery] = useState('')
  // The rail is an accelerator for a list already being read, so it keeps out
  // of the way until one is: a strip of letters over an unscrolled list is
  // chrome nobody asked for, and the search answers the same question better.
  const [railShown, setRailShown] = useState(false)
  // Where the list is, said in letters: the rail marks the heading the rows on
  // screen file under, whether the list was scrolled or scrubbed there.
  const readSection = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const top = event.nativeEvent.contentOffset.y
    if (top > RAIL_REVEAL_OFFSET) {
      setRailShown(true)
    }
    setReading(sectionAt(measured.offsets, measured.order, top - headerHeight))
  }
  const { showTopFade, scrollProps } = useScrollEdgeFades(readSection)
  const listRef = useRef<BottomSheetFlatListMethods>(null)
  // The rail scrubs to an offset, which is the search's height plus the rows
  // above the heading; only the first is the platform's to report.
  const [headerHeight, setHeaderHeight] = useState(0)
  const [reading, setReading] = useState<string>()
  // The offset the last touch asked for, and the frame that will honour it.
  const scrubbed = useRef<number | undefined>(undefined)
  const frame = useRef<number | undefined>(undefined)
  useEffect(
    () => () => {
      if (frame.current !== undefined) {
        cancelAnimationFrame(frame.current)
      }
    },
    [],
  )

  // Naming 245 countries is 245 trips through ICU, and collating them is 245
  // more. Held per locale rather than rebuilt on every keystroke.
  const sections = useMemo(
    () => countrySections(PHONE_COUNTRIES, locale),
    [locale],
  )
  const entries = useMemo(
    () =>
      countryListEntries(sections, query, selected, t('fiat.countryHeading')),
    [sections, query, selected, t],
  )
  const measured = useMemo(() => measureEntries(entries, HEIGHTS), [entries])
  // Every row's place, told rather than measured — see `measureEntries`.
  const getItemLayout = useCallback(
    (_data: ArrayLike<ListEntry> | null | undefined, index: number) => {
      const cell = measured.cells[index]
      return {
        index,
        length: cell?.length ?? HEIGHTS.row,
        offset: headerHeight + (cell?.offset ?? 0),
      }
    },
    [measured, headerHeight],
  )

  // Straight to the offset the finger is over, unanimated: an animation would
  // be chasing the drag instead of tracking it.
  // Stable, so the rail is not redrawn by every render of the list around it.
  const geometry = useRef({ measured, headerHeight })
  geometry.current = { measured, headerHeight }
  const scrubTo = useCallback((at: number, within: number) => {
    const { measured, headerHeight } = geometry.current
    const { offsets, order, height } = measured
    const start = offsets[order[at] ?? '']
    if (start === undefined) {
      return
    }
    const next = offsets[order[at + 1] ?? '']
    const end = next ?? height
    scrubbed.current = headerHeight + start + (end - start) * within
    if (frame.current !== undefined) {
      return
    }
    frame.current = requestAnimationFrame(() => {
      frame.current = undefined
      const offset = scrubbed.current
      if (offset !== undefined) {
        listRef.current?.scrollToOffset({ offset, animated: false })
      }
    })
  }, [])
  const measureHeader = ({ nativeEvent }: LayoutChangeEvent) =>
    setHeaderHeight(nativeEvent.layout.height)
  // Held steady so the rows are not all re-rendered when the letter the rail
  // marks changes: the host's handler is a fresh function on every render.
  const selectRef = useRef(onSelect)
  selectRef.current = onSelect
  const select = useCallback(
    (country: PhoneCountry) => selectRef.current(country),
    [],
  )
  const palette = useMemo(() => rowPalette(theme), [theme])
  const renderItem = useCallback(
    ({ item }: { item: ListEntry }) => (
      <ListRow
        entry={item}
        isSelected={item.kind === 'row' && item.key === selected.alpha2}
        palette={palette}
        onSelect={select}
      />
    ),
    [selected.alpha2, palette, select],
  )
  const listPadding = useMemo(
    () => ({
      paddingHorizontal: METRICS.screenInset,
      // Whatever is up already covers the home indicator.
      paddingBottom:
        Math.max(keyboardHeight, insets.bottom) + METRICS.listBottom,
    }),
    [keyboardHeight, insets.bottom],
  )
  return (
    // The sheet's own surface is the glass; the screen paints no fill.
    <Box style={{ height }}>
      <View style={styles.list}>
        <BottomSheetFlatList
          ref={listRef}
          data={entries}
          renderItem={renderItem}
          keyExtractor={entryKey}
          getItemLayout={getItemLayout}
          ListHeaderComponent={
            <Box style={styles.header} onLayout={measureHeader}>
              {heading === undefined ? null : (
                <Text weight="medium" style={styles.heading}>
                  {heading}
                </Text>
              )}
              <Box
                flexDirection="row"
                alignItems="center"
                style={[styles.search, { backgroundColor: theme.form.field }]}
              >
                <FigmaSearchIcon
                  size={16}
                  color={theme.content.elements.secondary}
                />
                <BottomSheetTextInput
                  value={query}
                  onChangeText={setQuery}
                  placeholder={t('fiat.countrySearchPlaceholder')}
                  placeholderTextColor={theme.content.elements.tertiary}
                  // Typing a name is the other way through the list; the rail
                  // stands down rather than covering the answers.
                  onFocus={() => setRailShown(false)}
                  accessibilityLabel={t('fiat.phoneCountrySearch')}
                  autoCapitalize="none"
                  autoCorrect={false}
                  spellCheck={false}
                  returnKeyType="search"
                  style={[
                    styles.searchInput,
                    { color: theme.content.elements.primary },
                  ]}
                />
              </Box>
            </Box>
          }
          // Scrolling the list is choosing by hand rather than by name, so
          // the field gives up focus and the keyboard goes with it —
          // `Keyboard.dismiss` blurs whatever holds it, which the drag-dismiss
          // mode alone does not.
          onScrollBeginDrag={dismissKeyboard}
          keyboardDismissMode="on-drag"
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
          initialNumToRender={INITIAL_ROWS}
          contentContainerStyle={listPadding}
          // Rows are cheap and their places are known, so the list can afford
          // to keep several screens either side mounted and to fill a landing
          // in few passes — a narrow window is what a fast scroll outruns.
          windowSize={WINDOW_SCREENS}
          maxToRenderPerBatch={ROWS_PER_BATCH}
          updateCellsBatchingPeriod={BATCH_PERIOD_MS}
          {...scrollProps}
        />

        {showTopFade ? (
          <ScrollFade
            edge="top"
            color={theme.background.modal}
            id="country-list"
          />
        ) : null}

        {/* Only over a list being scrolled: a search answers with one card,
            which no letter reaches into. */}
        {railShown && measured.order.length > 0 ? (
          <Animated.View
            pointerEvents="box-none"
            entering={FadeIn.duration(CONTENT_FADE_DURATION)}
            exiting={FadeOut.duration(CONTENT_FADE_DURATION)}
            style={styles.railSlot}
          >
            <FiatCountryIndexRail
              keys={measured.order}
              active={reading}
              onScrub={scrubTo}
            />
          </Animated.View>
        ) : null}

        <CountryCloseButton onPress={onClose} />
      </View>
    </Box>
  )
}

/**
 * The sheet's own way out (2861:12574), which the design gives the corner the
 * title used to hold.
 *
 * Over the list rather than in its header: the search scrolls away with the
 * rows, and the way out of 245 countries must not go with it.
 */
function CountryCloseButton({ onPress }: { onPress: () => void }) {
  const { t } = useFunkitTranslation()
  const theme = useTheme()
  const { pressStyle, onPressIn, onPressOut } = usePressScale()

  return (
    <Pressable
      onPress={onPress}
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      accessibilityRole="button"
      accessibilityLabel={t('common.close')}
      testID="country-sheet-close"
      style={styles.closeSlot}
    >
      <Animated.View
        style={[
          styles.close,
          { backgroundColor: theme.content.elements.ghosted },
          pressStyle,
        ]}
      >
        <FigmaCloseIcon
          size={METRICS.closeIcon}
          color={theme.content.elements.primary}
        />
      </Animated.View>
    </Pressable>
  )
}

const entryKey = (entry: ListEntry) => `${entry.kind}:${entry.key}`

/**
 * What a row costs, worked out once for the whole list.
 *
 * Restyle resolves its style props on every render, and a row drawn through it
 * is five of those plus five reads of the theme. At 245 rows that is what the
 * list cannot draw fast enough to keep a scroll filled, so the rows are plain
 * views over styles built here.
 */
interface RowPalette {
  card: ViewStyle
  divider: ViewStyle
  flag: TextStyle[]
  name: TextStyle[]
  section: TextStyle[]
  check: string
}

function rowPalette(theme: FunkitTheme): RowPalette {
  const base = flatTextStyle(theme)
  const medium = flatTextStyle(theme, { weight: 'medium' })
  return {
    card: { backgroundColor: theme.form.field },
    divider: { backgroundColor: theme.form.divider },
    flag: [...base, styles.flag],
    name: [...base, styles.name],
    section: [...medium, styles.sectionText],
    check: theme.brand.primary,
  }
}

// Memoised on the row itself: a scrub re-renders the list on every frame, and
// 245 rows redrawn per frame is what a finger outruns.
const ListRow = memo(function ListRow({
  entry,
  isSelected,
  palette,
  onSelect,
}: {
  entry: ListEntry
  isSelected: boolean
  palette: RowPalette
  onSelect: (country: PhoneCountry) => void
}) {
  if (entry.kind === 'header') {
    return (
      <View style={[styles.sectionTitle, entry.gap ? styles.sectionGap : null]}>
        <RNText style={palette.section}>{entry.key}</RNText>
      </View>
    )
  }

  const { country, name } = entry.item
  return (
    <View
      style={[
        styles.card,
        palette.card,
        entry.first ? styles.cardTop : null,
        entry.last ? styles.cardBottom : null,
      ]}
    >
      <Pressable
        onPress={() => onSelect(country)}
        accessibilityRole="button"
        // The calling code is what the row is chosen for, so it stays in the
        // label even though the design draws the name alone.
        accessibilityLabel={`${name} ${country.dialCode}`}
        accessibilityState={{ selected: isSelected }}
        style={styles.row}
      >
        <RNText style={palette.flag}>{countryFlag(country.alpha2)}</RNText>
        <RNText numberOfLines={1} style={palette.name}>
          {name}
        </RNText>
        {isSelected ? (
          <FigmaCheckIcon size={METRICS.check} color={palette.check} />
        ) : (
          <View style={styles.uncheckedSlot} />
        )}
      </Pressable>
      {entry.last ? null : <View style={[styles.divider, palette.divider]} />}
    </View>
  )
})

const dismissKeyboard = () => Keyboard.dismiss()

/** Far enough down to be a scroll rather than a bounce at the top. */
const RAIL_REVEAL_OFFSET = 8

/** Enough to fill the screen; the rest arrive as it is scrolled. */
const INITIAL_ROWS = 14

/** Screens of rows kept mounted around the viewport, and rows drawn per pass. */
const WINDOW_SCREENS = 11
const ROWS_PER_BATCH = 24
/** Short enough that a landing fills within a frame or two of arriving. */
const BATCH_PERIOD_MS = 16

/**
 * Read off 2184:14277, whose list frame is 345 wide on a 393 screen. The
 * chrome around it — grabber, close disc — is not in that frame; it takes the
 * geometry every other nested sheet's top bar uses.
 */
const METRICS = {
  screenInset: 24,
  rowHeight: 52,
  rowRadius: 26,
  rowPaddingHorizontal: 20,
  /** Flag glyph to the name beside it. */
  rowGap: 12,
  flagFontSize: 17,
  nameFontSize: 17,
  nameTracking: -0.34,
  check: 21,
  /** The rule a card rules its rows with. */
  dividerInset: 16,
  sectionHeight: 43,
  sectionFontSize: 17,
  sectionLineHeight: 22,
  sectionTracking: -0.43,
  sectionInset: 16,
  headingFontSize: 15,
  /** Between one section's card and the next section's heading (2290:2679). */
  sectionGap: 8,
  /** The disc the list is left by, below the grabber (2861:12574). */
  closeSize: 44,
  closeInset: 16,
  closeIcon: 16,
  /** Sheet top to the search, which clears the close disc above it. */
  searchTop: 92,
  /** Search to the first heading under it. */
  searchBottom: 21,
  /** The last row clears the sheet's foot by the gap a step's action does. */
  listBottom: 24,
  /** The rail is flush to the sheet's edge; the strip's own width insets it. */
  railRight: 0,
  /**
   * The band the rail scrubs, off both ends of the sheet — equal, so the
   * letters centre on the sheet as the design draws them, and clear of the
   * grabber above and the home indicator below.
   */
  railInset: 64,
} as const

/** What `sectionOffsets` measures the list with, read off METRICS. */
const HEIGHTS: EntryHeights = {
  row: METRICS.rowHeight,
  header: METRICS.sectionHeight,
  gap: METRICS.sectionGap,
}

const styles = StyleSheet.create({
  list: { flex: 1 },
  // Clear of the grabber above and the home indicator below, so a scrub never
  // starts on either.
  railSlot: {
    position: 'absolute',
    right: METRICS.railRight,
    top: METRICS.railInset,
    bottom: METRICS.railInset,
    alignItems: 'center',
  },
  // Below the grabber and the close disc the list scrolls under, so the search
  // clears both on the frame the sheet opens on.
  header: {
    paddingTop: METRICS.searchTop,
    paddingBottom: METRICS.searchBottom,
  },
  heading: {
    paddingHorizontal: METRICS.sectionInset,
    fontSize: METRICS.headingFontSize,
  },
  // Over the list, so the rows scroll under it rather than past it.
  closeSlot: {
    position: 'absolute',
    top: METRICS.closeInset,
    left: METRICS.closeInset,
    zIndex: 1,
  },
  close: {
    width: METRICS.closeSize,
    height: METRICS.closeSize,
    borderRadius: METRICS.closeSize / 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  search: {
    height: METRICS.rowHeight,
    borderRadius: METRICS.rowRadius,
    paddingHorizontal: METRICS.rowPaddingHorizontal,
    gap: METRICS.rowGap,
  },
  // Undo the platform's own padding so the field's height is the box's.
  searchInput: {
    flex: 1,
    padding: 0,
    fontSize: METRICS.nameFontSize,
    letterSpacing: METRICS.nameTracking,
  },
  sectionTitle: {
    height: METRICS.sectionHeight,
    paddingHorizontal: METRICS.sectionInset,
    justifyContent: 'center',
  },
  sectionGap: { marginTop: METRICS.sectionGap },
  sectionText: {
    fontSize: METRICS.sectionFontSize,
    lineHeight: METRICS.sectionLineHeight,
    letterSpacing: METRICS.sectionTracking,
  },
  // The fill is the card's, so the rule between two rows sits on it rather
  // than on the sheet behind them.
  card: { height: METRICS.rowHeight },
  cardTop: {
    borderTopLeftRadius: METRICS.rowRadius,
    borderTopRightRadius: METRICS.rowRadius,
  },
  cardBottom: {
    borderBottomLeftRadius: METRICS.rowRadius,
    borderBottomRightRadius: METRICS.rowRadius,
  },
  row: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: METRICS.rowPaddingHorizontal,
    gap: METRICS.rowGap,
  },
  flag: { fontSize: METRICS.flagFontSize },
  // The check's place, held open without drawing one: `SvgXml` parses its
  // markup per row, and 245 invisible checks is what blanks a fast scroll.
  // The design cuts every name at the same width (2184:14277), which the empty
  // slot keeps.
  uncheckedSlot: { width: METRICS.check },
  name: {
    flex: 1,
    fontSize: METRICS.nameFontSize,
    letterSpacing: METRICS.nameTracking,
  },
  // Absolute, so the rule costs the row no height and the pitch stays 52.
  divider: {
    position: 'absolute',
    bottom: 0,
    left: METRICS.dividerInset,
    right: METRICS.dividerInset,
    height: StyleSheet.hairlineWidth,
  },
})
